/**
 * @engine/vfx - Update Context Nodes
 *
 * Nodes for updating particles each frame.
 */

import type { VFXNodeDefinition } from '../../types';
import { dataPort, flowPort } from '../../types';

/**
 * Update context node definitions.
 */

export const FORCE_NODES: readonly VFXNodeDefinition[] = [
  // ---- Gravity ----
  {
    id: 'vfx.update.gravity',
    name: 'Gravity',
    category: 'force',
    contexts: ['update'],
    description: 'Apply constant gravity force',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('force', 'Force', 'vec3', { defaultValue: { x: 0, y: -9.8, z: 0 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `particle.velocity += force * deltaTime;`,
    },
  },

  // ---- Linear Drag ----
  {
    id: 'vfx.update.drag',
    name: 'Linear Drag',
    category: 'force',
    contexts: ['update'],
    description: 'Apply air resistance based on mass',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('dragCoefficient', 'Drag Coefficient', 'float', { defaultValue: 0.5, range: { min: 0, max: 10 } }),
      dataPort('useParticleSize', 'Use Particle Size', 'bool', { defaultValue: false }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        var drag = dragCoefficient;

        // Scale drag by particle size if enabled
        if (useParticleSize)
        {
            let side = particle.size * particle.scale;
            drag *= side.x * side.y;
        }

        // Exponential decay (mass not implemented yet — treated as 1)
        particle.velocity *= exp(-(drag * deltaTime) / 1.0);
      `,
    },
  },

  // ---- Turbulence ----
  {
    id: 'vfx.update.turbulence',
    name: 'Turbulence',
    category: 'force',
    contexts: ['update'],
    description: 'Apply noise-based turbulence force field',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('mode', 'Mode', 'int', { defaultValue: 0, options: ['Absolute', 'Relative'] }),
      dataPort('noiseType', 'Noise Type', 'int', { defaultValue: 0, options: ['Value', 'Perlin', 'Cellular'] }),
      dataPort('fieldOffset', 'Field Offset', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('fieldScale', 'Field Scale', 'vec3', { defaultValue: { x: 1, y: 1, z: 1 } }),
      dataPort('intensity', 'Intensity', 'float', { defaultValue: 1, range: { min: 0, max: 20 } }),
      dataPort('drag', 'Drag', 'float', { defaultValue: 0.5, range: { min: 0, max: 1 } }),
      dataPort('frequency', 'Frequency', 'float', { defaultValue: 1.5, range: { min: 0.01, max: 10 } }),
      dataPort('octaves', 'Octaves', 'int', { defaultValue: 3, range: { min: 1, max: 8 } }),
      dataPort('roughness', 'Roughness', 'float', { defaultValue: 0.5, range: { min: 0, max: 1 } }),
      dataPort('lacunarity', 'Lacunarity', 'float', { defaultValue: 2, range: { min: 1, max: 4 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        // Transform position into field space
        let fieldCoord = (particle.position - fieldOffset) * fieldScale;

        // Generate curl noise with octaves
        var turbForce = float3(0.0);
        var amp = 1.0;
        var freq = frequency;
        for (int i = 0; i < octaves; i++)
        {
            // noiseType: 0=Value, 1=Perlin, 2=Cellular
            if (noiseType == 0) {
                turbForce += valueCurlNoise3D(fieldCoord * freq) * amp;
            } else if (noiseType == 1) {
                turbForce += perlinCurlNoise3D(fieldCoord * freq) * amp;
            } else {
                turbForce += cellularCurlNoise3D(fieldCoord * freq) * amp;
            }
            freq *= lacunarity;
            amp *= roughness;
        }

        // Transform force back to world space and apply intensity
        let force = turbForce / fieldScale * intensity;

        // Apply force based on mode
        if (mode == 0) {
            // Absolute: direct velocity addition
            particle.velocity += force * deltaTime;
        } else {
            // Relative: blend towards target velocity with drag
            let targetVel = particle.velocity + force;
            particle.velocity = lerp(particle.velocity, targetVel, drag * deltaTime);
        }
      `,
    },
  },

  // ---- Curl Noise ----
  {
    id: 'vfx.update.curl_noise',
    name: 'Curl Noise',
    category: 'force',
    contexts: ['update'],
    description: 'Apply curl noise for fluid-like motion',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('noiseType', 'Noise Type', 'int', { defaultValue: 1, options: ['Value', 'Perlin', 'Cellular'] }),
      dataPort('dimensions', 'Dimensions', 'int', { defaultValue: 1, options: ['2D', '3D'] }),
      dataPort('amplitude', 'Amplitude', 'float', { defaultValue: 1, range: { min: 0, max: 10 } }),
      dataPort('frequency', 'Frequency', 'float', { defaultValue: 1, range: { min: 0.01, max: 10 } }),
      dataPort('octaves', 'Octaves', 'int', { defaultValue: 1, range: { min: 1, max: 8 } }),
      dataPort('roughness', 'Roughness', 'float', { defaultValue: 0.5, range: { min: 0, max: 1 } }),
      dataPort('lacunarity', 'Lacunarity', 'float', { defaultValue: 2, range: { min: 1, max: 4 } }),
      dataPort('speed', 'Speed', 'float', { defaultValue: 0, range: { min: 0, max: 10 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        // Animate coordinate over time
        let coord3D = particle.position * frequency + float3(simulationTime * speed);
        let coord2D = particle.position.xy * frequency + float2(simulationTime * speed);

        // Accumulate octaves
        var result3D = float3(0.0);
        var result2D = float2(0.0);
        var amp = 1.0;
        var freq = 1.0;

        for (int i = 0; i < octaves; i++)
        {
            if (dimensions == 0) {
                // 2D curl noise
                if (noiseType == 0) {
                    result2D += valueCurlNoise2D(coord2D * freq) * amp;
                } else if (noiseType == 1) {
                    result2D += perlinCurlNoise2D(coord2D * freq) * amp;
                } else {
                    result2D += cellularCurlNoise2D(coord2D * freq) * amp;
                }
            } else {
                // 3D curl noise
                if (noiseType == 0) {
                    result3D += valueCurlNoise3D(coord3D * freq) * amp;
                } else if (noiseType == 1) {
                    result3D += perlinCurlNoise3D(coord3D * freq) * amp;
                } else {
                    result3D += cellularCurlNoise3D(coord3D * freq) * amp;
                }
            }
            freq *= lacunarity;
            amp *= roughness;
        }

        // Apply amplitude and add to velocity
        if (dimensions == 0) {
            particle.velocity.x += result2D.x * amplitude * deltaTime;
            particle.velocity.y += result2D.y * amplitude * deltaTime;
        } else {
            particle.velocity += result3D * amplitude * deltaTime;
        }
      `,
    },
  },

  // ---- Wobble ----
  {
    id: 'vfx.update.wobble',
    name: 'Wobble',
    category: 'force',
    contexts: ['update'],
    description: 'Apply small noise-based position jitter',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('amplitude', 'Amplitude', 'float', { defaultValue: 0.1, range: { min: 0, max: 5 } }),
      dataPort('frequency', 'Frequency', 'float', { defaultValue: 2, range: { min: 0.1, max: 20 } }),
      dataPort('speed', 'Speed', 'float', { defaultValue: 3, range: { min: 0, max: 20 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let noisePos = particle.position * frequency + float3(simulationTime * speed);
        let wobbleOffset = float3(
            sin(noisePos.x + float(particle.seed) * 0.1) * amplitude,
            sin(noisePos.y + float(particle.seed) * 0.2 + 1.57) * amplitude,
            0.0
        );
        particle.position += wobbleOffset * deltaTime;
      `,
    },
  },

  // ---- Ripple ----
  {
    id: 'vfx.update.ripple',
    name: 'Ripple',
    category: 'force',
    contexts: ['update'],
    description: 'Apply expanding circular wave displacement',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('center', 'Center', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('amplitude', 'Amplitude', 'float', { defaultValue: 1, range: { min: 0, max: 10 } }),
      dataPort('wavelength', 'Wavelength', 'float', { defaultValue: 5, range: { min: 0.1, max: 50 } }),
      dataPort('speed', 'Speed', 'float', { defaultValue: 10, range: { min: 0, max: 50 } }),
      dataPort('decay', 'Decay', 'float', { defaultValue: 0.1, range: { min: 0, max: 1 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let toCenter = particle.position - center;
        let dist = length(toCenter.xy);
        let phase = dist / wavelength * 6.28318 - simulationTime * speed;
        let wave = sin(phase) * amplitude * exp(-dist * decay);
        particle.position.z = wave;
      `,
    },
  },

  // ---- Multi Ripple ----
  {
    id: 'vfx.update.multi_ripple',
    name: 'Multi Ripple',
    category: 'force',
    contexts: ['update'],
    description: 'Multiple random ripple sources',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('count', 'Sources', 'int', { defaultValue: 3, range: { min: 1, max: 8 } }),
      dataPort('radius', 'Spawn Radius', 'float', { defaultValue: 100, range: { min: 1, max: 500 } }),
      dataPort('amplitude', 'Amplitude', 'float', { defaultValue: 0.5, range: { min: 0, max: 5 } }),
      dataPort('wavelength', 'Wavelength', 'float', { defaultValue: 8, range: { min: 0.1, max: 50 } }),
      dataPort('speed', 'Speed', 'float', { defaultValue: 5, range: { min: 0, max: 50 } }),
      dataPort('decay', 'Decay', 'float', { defaultValue: 0.02, range: { min: 0, max: 0.5 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        var totalWave = 0.0;
        for (int i = 0; i < count; i++)
        {
            let seed = uint(i) * 12345u;
            let cx = (hash(seed) * 2.0 - 1.0) * radius;
            let cy = (hash(seed + 1u) * 2.0 - 1.0) * radius;
            let timeOffset = hash(seed + 2u) * 10.0;
            let toCenter = float2(particle.position.x - cx, particle.position.y - cy);
            let dist = length(toCenter);
            let phase = dist / wavelength * 6.28318 - (simulationTime + timeOffset) * speed;
            totalWave += sin(phase) * amplitude * exp(-dist * decay);
        }
        particle.position.z = totalWave;
      `,
    },
  },

  // ---- Attract / Repel ----
  {
    id: 'vfx.update.attract',
    name: 'Attract/Repel',
    category: 'force',
    contexts: ['update'],
    description: 'Attract or repel from a point',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('target', 'Target', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('strength', 'Strength', 'float', { defaultValue: 5, range: { min: -50, max: 50 } }),
      dataPort('maxDistance', 'Max Distance', 'float', { defaultValue: 10, range: { min: 0, max: 100 } }),
      dataPort('falloff', 'Falloff', 'int', { defaultValue: 1, options: ['Linear', 'Quadratic', 'None'] }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let dir = target - particle.position;
        let dist = length(dir);
        if (dist < maxDistance && dist > 0.001)
        {
            var factor = 1.0;
            if (falloff == 0) {
                factor = 1.0 - dist / maxDistance;
            } else if (falloff == 1) {
                factor = pow(1.0 - dist / maxDistance, 2.0);
            }
            particle.velocity += normalize(dir) * strength * factor * deltaTime;
        }
      `,
    },
  },

  // ---- Vortex ----
  {
    id: 'vfx.update.vortex',
    name: 'Vortex',
    category: 'force',
    contexts: ['update'],
    description: 'Apply vortex/spiral force',
    color: '#FF9800',
    inputs: [
      flowPort('exec', ''),
      dataPort('center', 'Center', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('axis', 'Axis', 'vec3', { defaultValue: { x: 0, y: 1, z: 0 } }),
      dataPort('strength', 'Strength', 'float', { defaultValue: 5, range: { min: 0, max: 50 } }),
      dataPort('pullStrength', 'Pull', 'float', { defaultValue: 0, range: { min: -10, max: 10 } }),
    ],
    outputs: [
      flowPort('next', ''),
    ],
    modifiesParticle: true,
    shader: {
      code: `
        let toCenter = particle.position - center;
        let distToCenter = length(toCenter);
        if (distToCenter > 0.001)
        {
            let toCenterNorm = toCenter / distToCenter;
            let tangent = cross(normalize(axis), toCenterNorm);
            particle.velocity += tangent * strength * deltaTime;
            particle.velocity -= toCenterNorm * pullStrength * deltaTime;
        }
      `,
    },
  },
];
